import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, concatMap, map, mergeMap, tap, withLatestFrom, filter } from 'rxjs/operators';

import { MentorService } from 'src/app/shared/services/mentor.service';
import { NotificationService } from '../../shared/services/notification.service';

import { of } from 'rxjs';
import { Mentor } from 'src/app/shared/types/mentor.types';
import {
  MentorLoad,
  MentorLoadError,
  MentorLoadSuccess,
  MENTOR_LOAD,
  MENTOR_LOAD_ERROR,
  MENTOR_LOAD_SUCCESS,
  UpdateMentor,
  UpdateMentorError,
  UpdateMentorSuccess,
  UPDATE_MENTOR,
  UPDATE_MENTOR_ERROR,
  UPDATE_MENTOR_SUCCESS,
} from '../actions/mentor.actions';
import { MenteeRelationLoad } from '../actions/mentee-relations.actions';
import { SessionsLoad, SessionsSetInitial } from '../actions/sessions.actions';
import { AppState } from '../reducers';
import { Store } from '@ngrx/store';
import { ConversationLoad } from '../actions/conversation.actions';

@Injectable()
export class MentorEffects {
  public mentorLoad$ = createEffect(() =>
    this.actions$.pipe(
      ofType<MentorLoad>(MENTOR_LOAD),
      tap(() => (this.notificationService.loading = true)),
      mergeMap(() =>
        this.mentorService.getMentorOfUser().pipe(
          map(data => {
            const mentor: Mentor = data;
            return new MentorLoadSuccess(mentor);
          }),
          catchError(() => of(new MentorLoadError()))
        )
      )
    )
  );

  public mentorLoadSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType<MentorLoadSuccess>(MENTOR_LOAD_SUCCESS),
      tap(() => (this.notificationService.loading = false)),
      filter((val: MentorLoadSuccess) => !!val.payload && !!val.payload.id),
      concatMap((val: MentorLoadSuccess) => [
        new MenteeRelationLoad([val.payload.id], 'mentor'),
        new SessionsSetInitial(),
        new SessionsLoad('mentor', [val.payload.id]),
        new ConversationLoad(),
      ])
    )
  );

  public mentorLoadError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<MentorLoadError>(MENTOR_LOAD_ERROR),
        tap(() => (this.notificationService.loading = false)),
        tap(() => this.notificationService.openSnackBar('error', 'COMMON.LOADING_MENTOR_DATA_FAILED'))
      ),
    { dispatch: false }
  );

  // Update mentor

  public updateMentor$ = createEffect(() =>
    this.actions$.pipe(
      ofType<UpdateMentor>(UPDATE_MENTOR),
      tap(() => (this.notificationService.loading = true)),
      mergeMap(payload =>
        this.mentorService.updateMentor(payload.mentorId, payload.mentor).pipe(
          map(mentor => {
            return new UpdateMentorSuccess(mentor);
          }),
          catchError(() => of(new UpdateMentorError(payload.mentorId)))
        )
      )
    )
  );

  public updateMentorSucces$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<UpdateMentorSuccess>(UPDATE_MENTOR_SUCCESS),
        tap(() => (this.notificationService.loading = false)),
        tap(() => this.notificationService.openSnackBar('success', 'COMMON.PROFILE_UPDATED')),
        withLatestFrom(this.store$.select(state => state.mentor))
      ),
    { dispatch: false }
  );

  public updateMentorError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<UpdateMentorError>(UPDATE_MENTOR_ERROR),
        tap(() => (this.notificationService.loading = false)),
        tap(() => this.notificationService.openSnackBar('error', 'COMMON.MENTOR_UPDATE_FAILED'))
      ),
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private notificationService: NotificationService,
    private mentorService: MentorService,
    private store$: Store<AppState>
  ) {}
}
